import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../../constants/config';
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from '../../../constants/paymentMethods';
import API from '../../../api/Api';

// The live customer order tracker. Given the opaque track_token returned at checkout, it polls
// GET /api/order/track/:token and shows the order's status as a stepper that advances on its own
// as the kitchen works the ticket — Pending → Accepted → Preparing → Ready → Completed (or a
// distinct Cancelled state). No login: the unguessable token is the credential.

// Mirrors the server STATUS_LABELS (order.service.js). 4=Completed / 5=Cancelled are terminal.
// Dots show a ✓ once a step is done and the step number otherwise, so no future step ever looks
// already-complete (the current step is set apart by the accent ring, not by a coloured glyph).

// A short, friendly "your order is ready" chime via WebAudio — the customer-side mirror of the
// kitchen's POS chime (NewOrders.jsx). No asset to ship; works in the built app. Fully guarded:
// if the browser blocks audio (no prior gesture) or has no AudioContext, it's a silent no-op and
// the visual banner + title flash still fire. A rising three-note arpeggio so it reads as "good news".
const playReadyChime = () => {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const tone = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t0 = ctx.currentTime + start;
            gain.gain.setValueAtTime(0.0001, t0);
            gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
            osc.start(t0);
            osc.stop(t0 + dur + 0.02);
        };
        tone(659, 0, 0.16);     // E5
        tone(880, 0.16, 0.16);  // A5
        tone(1175, 0.34, 0.30); // D6 — a happy rising "ta-da"
        setTimeout(() => { try { ctx.close(); } catch (e) { /* noop */ } }, 1000);
    } catch (e) {
        /* audio unavailable — silent fallback, the visual banner still fires */
    }
};

const OrderStatus = ({ token, onClose, currencySymbol }) => {
    const { t } = useTranslation();
    const STEPS = [
        { code: 0, label: t('order.step_received') },
        { code: 1, label: t('order.step_accepted') },
        { code: 2, label: t('order.step_preparing') },
        { code: 3, label: t('order.step_ready') },
        { code: 4, label: t('order.step_completed') },
    ];
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    // (B6) the diner's own dish ratings for this order — { item_id: stars }. Loaded once; a tap
    // upserts via POST /api/ratings (order-linked, so only their own dishes are accepted).
    const [myRatings, setMyRatings] = useState({});
    const [savingRating, setSavingRating] = useState(null);
    // "Your order is ready" alert: fires once, on the live transition INTO Ready (status 3) observed
    // during polling — never on first load, so re-opening a stale already-ready link stays quiet.
    const [readyAlert, setReadyAlert] = useState(false);
    const timerRef = useRef(null);
    const prevStatus = useRef(null);     // last seen status; null until the first fetch settles
    const titleTimer = useRef(null);     // tab-title flash interval (catches a backgrounded tab)
    const origTitle = useRef(typeof document !== 'undefined' ? document.title : '');

    const stopTitleFlash = useCallback(() => {
        if (titleTimer.current) { clearInterval(titleTimer.current); titleTimer.current = null; }
        if (typeof document !== 'undefined') document.title = origTitle.current;
    }, []);

    const triggerReadyAlert = useCallback(() => {
        setReadyAlert(true);
        playReadyChime();
        // Flash the tab title so a guest who switched away still notices their food is up.
        if (typeof document !== 'undefined' && !titleTimer.current) {
            let on = false;
            titleTimer.current = setInterval(() => {
                on = !on;
                document.title = on ? t('order.ready_tab_title') : origTitle.current;
            }, 1000);
        }
    }, [t]);

    const dismissReady = useCallback(() => {
        setReadyAlert(false);
        stopTitleFlash();
    }, [stopTitleFlash]);

    // (B6) Load the diner's existing ratings for this order once (already-rated dishes show filled).
    useEffect(() => {
        let alive = true;
        API.getData(`ratings/order/${token}`)
            .then(res => { if (alive && res.data?.status) setMyRatings(res.data.data || {}); })
            .catch(() => { /* ratings are optional — tracker works without them */ });
        return () => { alive = false; };
    }, [token]);

    // Tap-to-rate one ordered dish. Optimistic; on failure, resync from the server so the UI is honest.
    const rateDish = useCallback((itemId, stars) => {
        setSavingRating(itemId);
        setMyRatings(prev => ({ ...prev, [itemId]: stars }));
        API.postData('ratings', { track_token: token, item_id: itemId, rating: stars })
            .catch(() => {
                API.getData(`ratings/order/${token}`)
                    .then(res => { if (res.data?.status) setMyRatings(res.data.data || {}); })
                    .catch(() => {});
            })
            .finally(() => setSavingRating(null));
    }, [token]);

    const load = useCallback(async () => {
        try {
            const res = await API.getData(`order/track/${token}`);
            if (res.data?.status && res.data?.data) {
                const next = res.data.data;
                // Fire the ready alert only on a genuine forward transition into "Ready" (3) seen
                // while polling — guarded by prevStatus !== null so a first load that's already
                // ready (stale link) doesn't ring, and not for cancelled orders.
                const prev = prevStatus.current;
                if (prev !== null && prev < 3 && next.status >= 3 && next.status < 4 && !next.is_cancelled) {
                    triggerReadyAlert();
                }
                prevStatus.current = next.status;
                setData(next);
                setError('');
            } else {
                setError(res.data?.message || t('order.not_found'));
            }
        } catch (e) {
            setError(t('order.load_error'));
        } finally {
            setLoading(false);
        }
    }, [token, triggerReadyAlert, t]);

    useEffect(() => {
        if (!token) return;
        load();
        // Poll every 20s while the order is still open; stop once it's terminal.
        timerRef.current = setInterval(load, 20000);
        return () => clearInterval(timerRef.current);
    }, [token, load]);

    // Stop polling once the order reaches a terminal state (Completed / Cancelled).
    useEffect(() => {
        if (data && !data.is_open && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, [data]);

    // Always restore the original tab title when the tracker unmounts.
    useEffect(() => () => stopTitleFlash(), [stopTitleFlash]);

    if (loading && !data) {
        return <div className='mb-track-loading'>{t('order.loading')}</div>;
    }
    if (error && !data) {
        return (
            <div className='mb-cart-success'>
                <div className='mb-cart-success-emoji'>🧾</div>
                <h5>{t('order.placed_title')}</h5>
                <p>{t('order.placed_note')}</p>
                <button className='mb-cart-back' onClick={onClose}>{t('common.back_to_menu')}</button>
            </div>
        );
    }

    if (!data) return null; // backstop: never dereference data below if it isn't loaded yet
    const status = data.status;
    const cancelled = data.is_cancelled;
    const activeIdx = STEPS.findIndex(s => s.code === status);
    const headline = cancelled
        ? t('order.headline_cancelled')
        : status >= 4 ? t('order.headline_done')
        : status === 3 ? t('order.headline_ready')
        : status === 2 ? t('order.headline_preparing')
        : status === 1 ? t('order.headline_accepted')
        : t('order.headline_received');

    const payHint = PAYMENT_METHOD_LABELS[data.payment_method] || PAYMENT_METHOD_LABELS[PAYMENT_METHODS.AT_VENUE];
    // Currency symbol for the receipt. The track API returns the venue's own symbol, so this works
    // even when the tracker is opened in a fresh tab; fall back to the prop (live menu), then default.
    const sym = data.currency_symbol || currencySymbol || config.currencySymbol;

    return (
        <div className='mb-track'>
            <div className='mb-track-head'>
                <span className='mb-track-venue'>{data.venue_title || t('order.your_order_fallback')}</span>
                <span className='mb-track-order'>{t('order.order_number_label')}{data.order_number}</span>
            </div>

            <h5 className={`mb-track-headline${cancelled ? ' mb-track-headline-cancel' : ''}`}>{headline}</h5>

            {readyAlert && !cancelled && (
                <div className='mb-track-ready' role='status' aria-live='assertive'>
                    <span className='mb-track-ready-bell' aria-hidden='true'>🔔</span>
                    <span className='mb-track-ready-text'>{t('order.ready_alert')}</span>
                    <button className='mb-track-ready-x' onClick={dismissReady} aria-label={t('order.ready_dismiss')}>×</button>
                </div>
            )}

            {cancelled ? (
                <div className='mb-track-cancel'>{t('order.cancelled_note')}</div>
            ) : (
                <ol className='mb-track-steps' aria-label={t('order.progress_label')}>
                    {STEPS.map((s, i) => {
                        const state = i < activeIdx ? 'done' : i === activeIdx ? 'current' : 'todo';
                        return (
                            <li key={s.code} className={`mb-track-step mb-track-step-${state}`}>
                                <span className='mb-track-dot' aria-hidden='true'>{state === 'done' ? '✓' : i + 1}</span>
                                <span className='mb-track-step-label'>{s.label}</span>
                            </li>
                        );
                    })}
                </ol>
            )}

            {/* (B6d) Loyalty — progress toward the reward, or a "reward earned" banner. */}
            {data.loyalty && data.loyalty.enabled && data.loyalty.reward && (
                data.loyalty.rewards_available > 0 ? (
                    <div className='mb-loyalty-card mb-loyalty-won'>
                        🎉 {t('order.loyalty_earned', { reward: data.loyalty.reward, defaultValue: `You’ve earned ${data.loyalty.reward}!` })}
                        <span className='mb-loyalty-hint'>{t('order.loyalty_show_staff', 'Show this to staff to claim it.')}</span>
                    </div>
                ) : (
                    <div className='mb-loyalty-card'>
                        <div className='mb-loyalty-head'>
                            <span>🎟️ {data.loyalty.reward}</span>
                            <span className='mb-loyalty-count'>
                                {data.loyalty.model === 'stamps'
                                    ? `${data.loyalty.progress} / ${data.loyalty.target}`
                                    : `${sym}${data.loyalty.progress} / ${sym}${data.loyalty.target}`}
                            </span>
                        </div>
                        <div className='mb-loyalty-bar'>
                            <div className='mb-loyalty-fill' style={{ width: `${Math.min(100, (data.loyalty.progress / data.loyalty.target) * 100)}%` }} />
                        </div>
                        <div className='mb-loyalty-remain'>
                            {data.loyalty.model === 'stamps'
                                ? t('order.loyalty_remain_stamps', { n: Math.max(0, data.loyalty.target - data.loyalty.progress), defaultValue: `${Math.max(0, data.loyalty.target - data.loyalty.progress)} more to go` })
                                : t('order.loyalty_remain_spend', { amount: `${sym}${Math.max(0, data.loyalty.target - data.loyalty.progress).toFixed(2)}`, defaultValue: `${sym}${Math.max(0, data.loyalty.target - data.loyalty.progress).toFixed(2)} more to go` })}
                        </div>
                    </div>
                )
            )}

            {Array.isArray(data.items) && data.items.length > 0 && (
                <ul className='mb-track-items' aria-label={t('order.items_label')}>
                    {data.items.map((it, i) => (
                        <li key={i} className='mb-track-item'>
                            <div className='mb-track-item-row'>
                                <span className='mb-track-item-qty'>{it.quantity}×</span>
                                <span className='mb-track-item-name'>
                                    {it.name}
                                    {Array.isArray(it.modifiers) && it.modifiers.length > 0 && (
                                        <span className='mb-track-item-mods'>
                                            {it.modifiers.map(m => m.option).join(' · ')}
                                        </span>
                                    )}
                                </span>
                                <span className='mb-track-item-amt'>{sym}{parseFloat(it.amount || 0).toFixed(2)}</span>
                            </div>
                            {/* (B6) Order-linked star rating — the diner can rate each dish they had. */}
                            {it.item_id && (
                                <div className='mb-rate-row'>
                                    <span className='mb-rate-label'>
                                        {myRatings[it.item_id] ? t('rating.rated', 'Your rating') : t('rating.rate_prompt', 'Rate this dish')}
                                    </span>
                                    <span className='mb-rate-stars' role='radiogroup' aria-label={t('rating.rate_prompt', 'Rate this dish')}>
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <button
                                                key={n}
                                                type='button'
                                                className={`mb-rate-star${n <= (myRatings[it.item_id] || 0) ? ' on' : ''}`}
                                                aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
                                                aria-checked={n === myRatings[it.item_id]}
                                                role='radio'
                                                disabled={savingRating === it.item_id}
                                                onClick={() => rateDish(it.item_id, n)}
                                            >★</button>
                                        ))}
                                    </span>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {/* (B6b) Google-review nudge — only once the diner has loved something (a 5★) and the venue
                has set its review link. Turns in-app delight into public SEO for the venue. */}
            {data.google_review_url && Object.values(myRatings).some((v) => v === 5) && (
                <a className='mb-google-nudge' href={data.google_review_url} target='_blank' rel='noopener noreferrer'>
                    <span className='mb-google-nudge-txt'>🌟 {t('order.google_nudge', 'Loved it? A quick Google review means the world.')}</span>
                    <span className='mb-google-nudge-cta'>{t('order.google_nudge_cta', 'Review on Google')}</span>
                </a>
            )}

            <div className='mb-track-meta'>
                {data.customer_name && <div><strong>{data.customer_name}</strong>{data.table_no ? ` ${t('order.table_suffix', { table: data.table_no })}` : ''}</div>}
                <div>{t(data.item_count === 1 ? 'order.item_count' : 'order.item_count_plural', { count: data.item_count ?? 0, total: `${sym}${(parseFloat(data.total) || 0).toFixed(2)}`, payment: payHint })}</div>
                {data.notes && <div className='mb-track-note'>📝 {data.notes}</div>}
            </div>

            {!cancelled && data.is_open && (
                <p className='mb-track-live'>{t('order.live')}</p>
            )}

            <button className='mb-cart-back' onClick={onClose}>{t('common.back_to_menu')}</button>
        </div>
    );
};

export default OrderStatus;
